import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Megaphone,
  Pause,
  Play,
  Plus,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { SourceChannelPicker } from "@/components/inviter/SourceChannelPicker";
import {
  type AttemptStatus,
  type CampaignStrategy,
  type CampaignSummary,
  type PostPreview,
  useCampaign,
  useCampaignAttempts,
  useCampaignLifecycle,
  useCampaigns,
  useCreateCampaign,
  useDeleteCampaign,
  usePreviewPost,
  useRunCampaign,
} from "@/lib/inviter";
import { useCompanySettings } from "@/lib/companySettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SimpleSelect } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { ProgressBar } from "@/components/ui/progress";
import { ActionMenu } from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { ChannelQualityCard } from "@/components/inviter/ChannelQualityCard";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  ConfirmDialog,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerClose,
} from "@/components/ui/drawer";
import { formatRelative, formatDateTime } from "@/lib/format";
import { HelpSection } from "@/components/HelpSection";
import { pushToast } from "@/components/Toaster";

const STRATEGIES: CampaignStrategy[] = [
  "dm_message",
  "manual_add",
  "channel_forward",
  "story_mention",
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InviterPage() {
  const { t } = useTranslation();
  const campaigns = useCampaigns();
  const run = useRunCampaign();
  const pause = useCampaignLifecycle("pause");
  // A send in flight finishes first: say so, or "paused" with the row still
  // reading "queued" looks like the button did nothing.
  const onPause = (id: string) =>
    pause.mutate(id, {
      onSuccess: (c) => {
        if (c.status !== "paused") {
          pushToast({ kind: "info", text: t("inviter.pause_requested") });
        }
      },
    });
  const resume = useCampaignLifecycle("resume");
  const cancel = useCampaignLifecycle("cancel");
  const del = useDeleteCampaign();

  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<CampaignSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CampaignSummary | null>(null);

  const data = useMemo(() => campaigns.data ?? [], [campaigns.data]);

  // A link from elsewhere opens a campaign's card: /inviter?campaign=<id>
  // (the inbox's "from: <campaign>"). Also when this page is already open.
  useEffect(() => {
    const openFromUrl = () => {
      const id = new URLSearchParams(window.location.search).get("campaign");
      if (id) {
        setDetailId(id);
        setDrawerOpen(true);
      }
    };
    openFromUrl();
    window.addEventListener("popstate", openFromUrl);
    return () => window.removeEventListener("popstate", openFromUrl);
  }, []);

  const openDetail = (c: CampaignSummary) => {
    setDetailId(c.id);
    setDrawerOpen(true);
  };

  const onCancel = async () => {
    if (!cancelTarget) return;
    try {
      await cancel.mutateAsync(cancelTarget.id);
      pushToast({ kind: "success", text: t("inviter.status.cancelled") });
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("inviter.action_error"),
      });
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await del.mutateAsync(deleteTarget.id);
      pushToast({ kind: "success", text: t("inviter.delete") });
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("inviter.action_error"),
      });
    }
  };

  const columns = useMemo<ColumnDef<CampaignSummary, unknown>[]>(
    () => [
      {
        id: "name",
        header: t("inviter.col.name"),
        accessorFn: (c) => c.name,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="min-w-0">
              <span className="block truncate font-semibold text-fg-primary">
                {c.name}
              </span>
              <span className="block truncate font-mono text-xs text-fg-muted">
                {c.target_channel_input}
              </span>
            </div>
          );
        },
      },
      {
        id: "strategy",
        header: t("inviter.redesign.col_strategy"),
        accessorFn: (c) => c.strategy,
        cell: ({ row }) => (
          <Badge tone="info" variant="subtle">
            {t(`inviter.redesign.strategy_short.${row.original.strategy}`)}
          </Badge>
        ),
      },
      {
        id: "progress",
        header: t("inviter.col.progress"),
        enableSorting: false,
        cell: ({ row }) => {
          const c = row.original;
          const failed =
            c.failed_privacy + c.failed_peer_flood + c.failed_admin + c.failed_other;
          return (
            <div className="min-w-[140px] space-y-1">
              <ProgressBar
                value={c.attempted}
                max={Math.max(1, c.total_targets)}
                tone="accent"
                compact
              />
              <div className="flex items-center justify-between gap-2 text-xs text-fg-muted">
                <span className="font-mono">
                  {t("inviter.redesign.progress_of", {
                    done: c.attempted,
                    total: c.total_targets,
                  })}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-success">+{c.succeeded}</span>
                  <span className="text-danger">−{failed}</span>
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "created",
        header: t("inviter.redesign.col_created"),
        // Two relative timestamps are the least urgent column here; dropping
        // them below ~1400px keeps Статус and the actions on screen.
        meta: { className: "hidden min-[1400px]:table-cell" },
        accessorFn: (c) => c.started_at ?? c.created_at,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="text-xs text-fg-muted">
              <div>
                {t("inviter.redesign.created_at", {
                  when: formatRelative(c.created_at),
                })}
              </div>
              <div>
                {c.started_at
                  ? t("inviter.redesign.started_at", {
                      when: formatRelative(c.started_at),
                    })
                  : c.status === "queued"
                    ? t("inviter.redesign.starts_itself")
                    : t("inviter.redesign.not_started")}
              </div>
            </div>
          );
        },
      },
      {
        id: "status",
        header: t("inviter.col.status"),
        accessorFn: (c) => c.status,
        cell: ({ row }) => {
          const c = row.original;
          const badge = (
            <StatusBadge
              domain="campaign"
              value={c.status}
              label={t(`inviter.status.${c.status}`)}
              dot
            />
          );
          if (c.error) {
            return (
              <div className="flex items-center gap-1.5">
                <Tooltip content={c.error}>{badge}</Tooltip>
                <Tooltip content={c.error}>
                  <XCircle
                    size={14}
                    strokeWidth={2}
                    aria-hidden="true"
                    className="text-danger"
                  />
                </Tooltip>
              </div>
            );
          }
          if (c.waiting_reason) {
            return (
              <div className="space-y-1">
                {badge}
                <div className="max-w-[14rem] text-xs text-fg-muted">
                  {t(`inviter.waiting.${c.waiting_reason}`)}
                </div>
              </div>
            );
          }
          return badge;
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("inviter.col.actions")}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const c = row.original;
          const runnable = c.status === "queued" || c.status === "running";
          const cancellable = c.status !== "done" && c.status !== "cancelled";

          // Primary inline action: Pause (queued/running) or Resume (paused).
          // There is no "Run": the worker picks a created campaign up by
          // itself within half a minute.
          let primary: React.ReactNode = null;
          if (runnable) {
            primary = (
              <Button
                size="sm"
                variant="outline"
                loading={pause.isPending && pause.variables === c.id}
                disabled={c.waiting_reason === "pausing"}
                onClick={() => onPause(c.id)}
                iconLeft={<Pause size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("inviter.pause")}
              </Button>
            );
          } else if (c.status === "paused") {
            primary = (
              <Button
                size="sm"
                variant="outline"
                onClick={() => resume.mutate(c.id)}
                iconLeft={<Play size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("inviter.resume")}
              </Button>
            );
          }

          const menuItems = [];
          if (cancellable) {
            menuItems.push({
              label: t("inviter.cancel"),
              icon: <XCircle size={15} strokeWidth={2} aria-hidden="true" />,
              onSelect: () => setCancelTarget(c),
            });
          }
          menuItems.push({
            label: t("inviter.delete"),
            icon: <Trash2 size={15} strokeWidth={2} aria-hidden="true" />,
            onSelect: () => setDeleteTarget(c),
            destructive: true,
            separatorBefore: menuItems.length > 0,
          });

          return (
            <div
              className="flex items-center justify-end gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {primary}
              <ActionMenu label={t("inviter.col.actions")} items={menuItems} />
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, run.isPending],
  );

  const subtitle = useMemo(() => {
    if (data.length === 0) return t("inviter.subtitle");
    const active = data.filter(
      (c) => c.status === "running" || c.status === "queued",
    ).length;
    return t("inviter.redesign.subtitle_counts", { total: data.length, active });
  }, [data, t]);

  return (
    <TooltipProvider delayDuration={300}>
      <section className="mx-auto max-w-7xl space-y-6 px-6 py-8">
        <PageHeader
          title={t("inviter.title")}
          subtitle={subtitle}
          actions={
            <Button
              onClick={() => setShowCreate(true)}
              iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
            >
              {t("inviter.create")}
            </Button>
          }
        />

        {campaigns.isLoading && (
          <p className="text-fg-muted">{t("inviter.loading")}</p>
        )}
        {campaigns.isError && (
          <p className="text-danger">{t("inviter.redesign.load_error")}</p>
        )}

        {campaigns.data && (
          <DataTable
            columns={columns}
            data={data}
            getRowId={(c) => c.id}
            onRowClick={openDetail}
            empty={
              <EmptyState
                icon={Megaphone}
                title={t("inviter.empty_title")}
                description={t("inviter.empty_hint")}
                action={
                  <Button
                    onClick={() => setShowCreate(true)}
                    iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("inviter.create")}
                  </Button>
                }
              />
            }
          />
        )}

        <ChannelQualityCard />

        <HelpSection
          i18nKey="inviter.help"
          paragraphs={4}
          list={{
            titleKey: "inviter.help.list_title",
            itemsKey: "inviter.help.list_items",
            itemCount: 4,
          }}
        />

        <CreateCampaignWizard open={showCreate} onOpenChange={setShowCreate} />

        <CampaignDetailDrawer
          id={detailId}
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
        />

        <ConfirmDialog
          open={cancelTarget !== null}
          onOpenChange={(o) => !o && setCancelTarget(null)}
          tone="danger"
          title={t("inviter.cancel_confirm", { name: cancelTarget?.name ?? "" })}
          confirmText={t("inviter.cancel")}
          cancelText={t("common.cancel")}
          onConfirm={onCancel}
        />
        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          tone="danger"
          title={t("inviter.delete_confirm", { name: deleteTarget?.name ?? "" })}
          confirmText={t("inviter.delete")}
          cancelText={t("common.cancel")}
          onConfirm={onDelete}
        />
      </section>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Create wizard (3-step Dialog, local stepper — no route)
// ---------------------------------------------------------------------------

const STEP_COUNT = 3;

function CreateCampaignWizard({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateCampaign();
  const previewPost = usePreviewPost();
  const settings = useCompanySettings();

  const [step, setStep] = useState(1);

  // Step 1 — target + strategy
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [strategy, setStrategy] = useState<CampaignStrategy>("dm_message");

  // Default strategy from company settings (Settings → Привлечение → Инвайтер),
  // seeded when the wizard opens.
  useEffect(() => {
    if (open && settings.data) {
      setStrategy(settings.data.inviter.default_strategy as CampaignStrategy);
    }
  }, [open, settings.data]);

  // Step 2 — strategy params
  const [dmTemplate, setDmTemplate] = useState(
    "Привет! Заходи в канал: {channel_link}",
  );
  const [forwardPostUrl, setForwardPostUrl] = useState("");
  const [forwardFollowup, setForwardFollowup] = useState("");
  const [forwardQuoteEnabled, setForwardQuoteEnabled] = useState(false);
  const [forwardQuoteText, setForwardQuoteText] = useState("");
  const [sourceChannels, setSourceChannels] = useState<string[]>([]);
  const [sourceImports, setSourceImports] = useState<string[]>([]);
  const [forwardPreview, setForwardPreview] = useState<PostPreview | null>(null);
  const [forwardPreviewError, setForwardPreviewError] = useState<string | null>(
    null,
  );
  const [storyAssetId, setStoryAssetId] = useState("");
  const [storyCaption, setStoryCaption] = useState("");
  const [storyCtaUrl, setStoryCtaUrl] = useState("");
  const [storyBatchSize, setStoryBatchSize] = useState("20");

  // Step 3 — audience + delays
  const [maxTargets, setMaxTargets] = useState("100");
  const [minDelay, setMinDelay] = useState("60");
  const [maxDelay, setMaxDelay] = useState("180");
  const [onlineRecently, setOnlineRecently] = useState<boolean | undefined>(false);
  const [hasUsername, setHasUsername] = useState(true);
  const [onlyPremium, setOnlyPremium] = useState(false);
  const [excludeContacted, setExcludeContacted] = useState(true);
  const [excludeContactedDays, setExcludeContactedDays] = useState("30");
  const [excludeToxic, setExcludeToxic] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setStep(1);
    setName("");
    setTarget("");
    setStrategy("dm_message");
    setDmTemplate("Привет! Заходи в канал: {channel_link}");
    setForwardPostUrl("");
    setForwardFollowup("");
    setForwardQuoteEnabled(false);
    setForwardQuoteText("");
    setForwardPreview(null);
    setForwardPreviewError(null);
    setStoryAssetId("");
    setStoryCaption("");
    setStoryCtaUrl("");
    setStoryBatchSize("20");
    setMaxTargets("100");
    setMinDelay("60");
    setMaxDelay("180");
    setOnlineRecently(false);
    setHasUsername(true);
    setOnlyPremium(false);
    setExcludeContacted(true);
    setExcludeContactedDays("30");
    setExcludeToxic(true);
    setError(null);
  };

  const handleOpenChange = (o: boolean) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const onLoadPreview = async () => {
    setForwardPreviewError(null);
    if (!forwardPostUrl.trim()) return;
    try {
      const p = await previewPost.mutateAsync(forwardPostUrl.trim());
      setForwardPreview(p);
    } catch (err) {
      setForwardPreviewError(
        err instanceof ApiError ? err.detail : t("inviter.field.preview_failed"),
      );
      setForwardPreview(null);
    }
  };

  // Mouse-select handler. Highlighting a chunk of the preview text mirrors it
  // into quote_text and auto-enables quote mode (they can untick to revert).
  const onPreviewSelect = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const text = sel.toString();
    if (text.length < 5) return;
    setForwardQuoteText(text);
    setForwardQuoteEnabled(true);
  };

  // Per-step validation gate. Returns null when valid, else an error message.
  const validateStep = (s: number): string | null => {
    if (s === 1) {
      if (!name.trim()) return t("inviter.redesign.name_required");
      if (!target.trim()) return t("inviter.redesign.target_required");
      return null;
    }
    if (s === 2) {
      if (strategy === "dm_message" && !dmTemplate.trim())
        return t("inviter.field.dm_template_required");
      if (strategy === "channel_forward") {
        if (!forwardPostUrl.trim())
          return t("inviter.field.forward_post_required");
        if (forwardQuoteEnabled && forwardQuoteText.trim().length < 5)
          return t("inviter.field.forward_quote_too_short");
        if (forwardQuoteEnabled && !forwardFollowup.trim())
          return t("inviter.field.forward_quote_body_required");
      }
      if (strategy === "story_mention" && !storyAssetId.trim())
        return t("inviter.field.story_asset_required");
      return null;
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setStep((s) => Math.min(STEP_COUNT, s + 1));
  };

  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(1, s - 1));
  };

  const onSubmit = async () => {
    for (let s = 1; s <= STEP_COUNT; s += 1) {
      const err = validateStep(s);
      if (err) {
        setError(err);
        setStep(s);
        return;
      }
    }
    setError(null);
    try {
      await create.mutateAsync({
        name: name.trim(),
        target_channel: target.trim(),
        strategy,
        dm_template: strategy === "dm_message" ? dmTemplate.trim() : undefined,
        forward_post_url:
          strategy === "channel_forward" ? forwardPostUrl.trim() : undefined,
        forward_followup_text:
          strategy === "channel_forward" && forwardFollowup.trim()
            ? forwardFollowup.trim()
            : undefined,
        forward_quote_text:
          strategy === "channel_forward" &&
          forwardQuoteEnabled &&
          forwardQuoteText.trim()
            ? forwardQuoteText.trim()
            : undefined,
        story_media_asset_id:
          strategy === "story_mention" ? storyAssetId.trim() : undefined,
        story_caption:
          strategy === "story_mention" && storyCaption.trim()
            ? storyCaption.trim()
            : undefined,
        story_cta_channel_post_url:
          strategy === "story_mention" && storyCtaUrl.trim()
            ? storyCtaUrl.trim()
            : undefined,
        story_batch_size:
          strategy === "story_mention" ? Number(storyBatchSize) || 20 : undefined,
        no_bots: true,
        channel_ids: sourceChannels.length > 0 ? sourceChannels : undefined,
        import_sources: sourceImports.length > 0 ? sourceImports : undefined,
        has_username: hasUsername || undefined,
        is_premium: onlyPremium || undefined,
        online_recently: onlineRecently,
        exclude_contacted_days:
          excludeContacted && Number(excludeContactedDays) > 0
            ? Number(excludeContactedDays)
            : undefined,
        exclude_toxic_sources: excludeToxic,
        max_targets: maxTargets ? Number(maxTargets) : undefined,
        min_delay_seconds: Number(minDelay) || 60,
        max_delay_seconds: Number(maxDelay) || 180,
      });
      pushToast({ kind: "success", text: t("inviter.create_submit") });
      handleOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("inviter.create_error"));
    }
  };

  const stepTitle =
    step === 1
      ? t("inviter.redesign.step1_title")
      : step === 2
        ? t("inviter.redesign.step2_title")
        : t("inviter.redesign.step3_title");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("inviter.redesign.wizard_title")}</DialogTitle>
          <DialogDescription>{stepTitle}</DialogDescription>
          <Stepper current={step} />
        </DialogHeader>

        <DialogBody className="space-y-5">
          {step === 1 && (
            <Step1
              name={name}
              setName={setName}
              target={target}
              setTarget={setTarget}
              strategy={strategy}
              setStrategy={setStrategy}
            />
          )}
          {step === 2 && (
            <Step2
              strategy={strategy}
              dmTemplate={dmTemplate}
              setDmTemplate={setDmTemplate}
              forwardPostUrl={forwardPostUrl}
              setForwardPostUrl={setForwardPostUrl}
              forwardFollowup={forwardFollowup}
              setForwardFollowup={setForwardFollowup}
              forwardQuoteEnabled={forwardQuoteEnabled}
              setForwardQuoteEnabled={setForwardQuoteEnabled}
              forwardQuoteText={forwardQuoteText}
              setForwardQuoteText={setForwardQuoteText}
              forwardPreview={forwardPreview}
              setForwardPreview={setForwardPreview}
              forwardPreviewError={forwardPreviewError}
              onLoadPreview={onLoadPreview}
              onPreviewSelect={onPreviewSelect}
              previewLoading={previewPost.isPending}
              storyAssetId={storyAssetId}
              setStoryAssetId={setStoryAssetId}
              storyCaption={storyCaption}
              setStoryCaption={setStoryCaption}
              storyCtaUrl={storyCtaUrl}
              setStoryCtaUrl={setStoryCtaUrl}
              storyBatchSize={storyBatchSize}
              setStoryBatchSize={setStoryBatchSize}
            />
          )}
          {step === 3 && (
            <Step3
              sourceChannels={sourceChannels}
              setSourceChannels={setSourceChannels}
              sourceImports={sourceImports}
              setSourceImports={setSourceImports}
              maxTargets={maxTargets}
              setMaxTargets={setMaxTargets}
              minDelay={minDelay}
              setMinDelay={setMinDelay}
              maxDelay={maxDelay}
              setMaxDelay={setMaxDelay}
              hasUsername={hasUsername}
              setHasUsername={setHasUsername}
              onlyPremium={onlyPremium}
              setOnlyPremium={setOnlyPremium}
              onlineRecently={onlineRecently}
              setOnlineRecently={setOnlineRecently}
              excludeContacted={excludeContacted}
              setExcludeContacted={setExcludeContacted}
              excludeContactedDays={excludeContactedDays}
              setExcludeContactedDays={setExcludeContactedDays}
              excludeToxic={excludeToxic}
              setExcludeToxic={setExcludeToxic}
            />
          )}

          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>

        <DialogFooter>
          <span className="mr-auto text-xs text-fg-muted">
            {t("inviter.redesign.step_of", { current: step, total: STEP_COUNT })}
          </span>
          {step > 1 && (
            <Button variant="outline" onClick={goBack}>
              {t("inviter.redesign.back")}
            </Button>
          )}
          {step < STEP_COUNT ? (
            <Button onClick={goNext}>{t("inviter.redesign.next")}</Button>
          ) : (
            <Button loading={create.isPending} onClick={onSubmit}>
              {t("inviter.create_submit")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ current }: { current: number }) {
  const { t } = useTranslation();
  const labels = [
    t("inviter.redesign.step1_label"),
    t("inviter.redesign.step2_label"),
    t("inviter.redesign.step3_label"),
  ];
  return (
    <ol className="mt-3 flex items-center gap-2">
      {labels.map((label, i) => {
        const n = i + 1;
        const active = n === current;
        const done = n < current;
        return (
          <li
            key={label}
            className={
              "flex min-w-0 items-center gap-2 " +
              (active ? "flex-[2_1_0%] sm:flex-1" : "flex-1")
            }
          >
            <span
              className={
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold " +
                (active
                  ? "bg-accent text-fg-on-accent"
                  : done
                    ? "bg-success text-fg-on-accent"
                    : "bg-bg-elevated text-fg-muted")
              }
            >
              {n}
            </span>
            <span
              className={
                "truncate text-xs " +
                (active
                  ? "font-medium text-fg-primary"
                  : "hidden text-fg-muted sm:inline")
              }
            >
              {label}
            </span>
            {n < labels.length && (
              <span className="h-px flex-1 bg-border" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ----- Step 1: target + strategy -----

function Step1({
  name,
  setName,
  target,
  setTarget,
  strategy,
  setStrategy,
}: {
  name: string;
  setName: (v: string) => void;
  target: string;
  setTarget: (v: string) => void;
  strategy: CampaignStrategy;
  setStrategy: (v: CampaignStrategy) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="c_name">{t("inviter.field.name")}</Label>
        <Input
          id="c_name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("inviter.field.name_placeholder") ?? ""}
        />
      </div>

      {/* Strategy first: it decides what a valid target even is. Asking for
          the target before the strategy is how operators end up typing a
          broadcast channel into a manual_add campaign, which TG always
          refuses. */}
      <div className="space-y-2">
        <Label>{t("inviter.field.strategy")}</Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {STRATEGIES.map((s) => (
            <StrategyCard
              key={s}
              value={s}
              active={strategy === s}
              onSelect={() => setStrategy(s)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="c_target">{t(TARGET_I18N[strategy].label)}</Label>
        <Input
          id="c_target"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder={TARGET_I18N[strategy].placeholder}
        />
        <p className="text-xs text-fg-muted">{t(TARGET_I18N[strategy].hint)}</p>
      </div>
    </div>
  );
}

// What counts as a valid target depends entirely on the strategy — manual_add
// only works against a supergroup, everything else accepts a channel too.
const TARGET_I18N: Record<
  CampaignStrategy,
  { label: string; hint: string; placeholder: string }
> = {
  manual_add: {
    label: "inviter.field.target_add",
    hint: "inviter.field.target_hint_add",
    placeholder: "@my_supergroup",
  },
  dm_message: {
    label: "inviter.field.target_dm",
    hint: "inviter.field.target_hint_dm",
    placeholder: "@my_channel",
  },
  channel_forward: {
    label: "inviter.field.target_dm",
    hint: "inviter.field.target_hint_dm",
    placeholder: "@my_channel",
  },
  story_mention: {
    label: "inviter.field.target_dm",
    hint: "inviter.field.target_hint_dm",
    placeholder: "@my_channel",
  },
};

const STRATEGY_I18N: Record<
  CampaignStrategy,
  { title: string; hint: string; beta?: boolean }
> = {
  dm_message: {
    title: "inviter.field.strategy_dm",
    hint: "inviter.field.strategy_dm_hint",
  },
  manual_add: {
    title: "inviter.field.strategy_add",
    hint: "inviter.field.strategy_add_hint",
  },
  channel_forward: {
    title: "inviter.field.strategy_forward",
    hint: "inviter.field.strategy_forward_hint",
  },
  story_mention: {
    title: "inviter.field.strategy_story",
    hint: "inviter.field.strategy_story_hint",
    beta: true,
  },
};

function StrategyCard({
  value,
  active,
  onSelect,
}: {
  value: CampaignStrategy;
  active: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const meta = STRATEGY_I18N[value];
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={
        "rounded-xl border p-3 text-left transition-colors " +
        (active
          ? "border-accent bg-accent/15"
          : "border-border hover:border-border-strong hover:bg-bg-elevated/40")
      }
    >
      <div className="flex items-center gap-2">
        <span
          className={
            "text-sm font-medium " + (active ? "text-accent" : "text-fg-primary")
          }
        >
          {t(meta.title)}
        </span>
        {meta.beta && (
          <Badge tone="warning" variant="subtle" className="uppercase">
            beta
          </Badge>
        )}
      </div>
      <p className="mt-1 text-xs text-fg-muted">{t(meta.hint)}</p>
    </button>
  );
}

// ----- Step 2: strategy params -----

interface Step2Props {
  strategy: CampaignStrategy;
  dmTemplate: string;
  setDmTemplate: (v: string) => void;
  forwardPostUrl: string;
  setForwardPostUrl: (v: string) => void;
  forwardFollowup: string;
  setForwardFollowup: (v: string) => void;
  forwardQuoteEnabled: boolean;
  setForwardQuoteEnabled: (v: boolean) => void;
  forwardQuoteText: string;
  setForwardQuoteText: (v: string) => void;
  forwardPreview: PostPreview | null;
  setForwardPreview: (v: PostPreview | null) => void;
  forwardPreviewError: string | null;
  onLoadPreview: () => void;
  onPreviewSelect: () => void;
  previewLoading: boolean;
  storyAssetId: string;
  setStoryAssetId: (v: string) => void;
  storyCaption: string;
  setStoryCaption: (v: string) => void;
  storyCtaUrl: string;
  setStoryCtaUrl: (v: string) => void;
  storyBatchSize: string;
  setStoryBatchSize: (v: string) => void;
}

const TEXTAREA_CLASS =
  "w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

function Step2(props: Step2Props) {
  const { t } = useTranslation();
  const { strategy } = props;

  if (strategy === "manual_add") {
    return <Alert tone="info">{t("inviter.redesign.manual_add_note")}</Alert>;
  }

  if (strategy === "dm_message") {
    return (
      <div className="space-y-2">
        <Label htmlFor="c_dm">{t("inviter.field.dm_template")}</Label>
        <textarea
          id="c_dm"
          value={props.dmTemplate}
          onChange={(e) => props.setDmTemplate(e.target.value)}
          rows={4}
          placeholder={t("inviter.field.dm_template_placeholder") ?? ""}
          className={TEXTAREA_CLASS + " font-mono"}
        />
        <p className="text-xs text-fg-muted">{t("inviter.field.dm_template_hint")}</p>
      </div>
    );
  }

  if (strategy === "story_mention") {
    return (
      <div className="space-y-4">
        <Alert tone="warning">{t("inviter.field.story_premium_note")}</Alert>
        <div className="space-y-2">
          <Label htmlFor="c_story_asset">{t("inviter.field.story_asset")}</Label>
          <Input
            id="c_story_asset"
            value={props.storyAssetId}
            onChange={(e) => props.setStoryAssetId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="font-mono"
          />
          <p className="text-xs text-fg-muted">{t("inviter.field.story_asset_hint")}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="c_story_caption">{t("inviter.field.story_caption")}</Label>
          <textarea
            id="c_story_caption"
            value={props.storyCaption}
            onChange={(e) => props.setStoryCaption(e.target.value)}
            rows={2}
            maxLength={500}
            className={TEXTAREA_CLASS}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="c_story_cta">{t("inviter.field.story_cta")}</Label>
          <Input
            id="c_story_cta"
            value={props.storyCtaUrl}
            onChange={(e) => props.setStoryCtaUrl(e.target.value)}
            placeholder="https://t.me/my_channel/42"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="c_story_batch">{t("inviter.field.story_batch")}</Label>
          <Input
            id="c_story_batch"
            inputMode="numeric"
            value={props.storyBatchSize}
            onChange={(e) =>
              props.setStoryBatchSize(e.target.value.replace(/\D/g, ""))
            }
            className="max-w-[8rem] font-mono"
          />
          <p className="text-xs text-fg-muted">{t("inviter.field.story_batch_hint")}</p>
        </div>
      </div>
    );
  }

  // channel_forward — roomy step with preview + quote mode.
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="c_fwd_url">{t("inviter.field.forward_post_url")}</Label>
        <div className="flex gap-2">
          <Input
            id="c_fwd_url"
            value={props.forwardPostUrl}
            onChange={(e) => {
              props.setForwardPostUrl(e.target.value);
              if (props.forwardPreview) props.setForwardPreview(null);
            }}
            placeholder="https://t.me/my_channel/42"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={props.onLoadPreview}
            loading={props.previewLoading}
            disabled={!props.forwardPostUrl.trim()}
          >
            {t("inviter.field.preview_load")}
          </Button>
        </div>
        <p className="text-xs text-fg-muted">{t("inviter.field.forward_post_hint")}</p>
      </div>

      {props.forwardPreviewError && (
        <Alert tone="danger">{props.forwardPreviewError}</Alert>
      )}

      {props.forwardPreview && (
        <Card className="space-y-2 border-accent/30 bg-accent/5 p-3">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-accent">
              {props.forwardPreview.channel_title ||
                `@${props.forwardPreview.channel_username}`}
            </span>
            <span className="flex items-center gap-2 text-fg-muted">
              <span className="font-mono">#{props.forwardPreview.message_id}</span>
              {props.forwardPreview.has_media && (
                <Badge tone="neutral" variant="subtle">
                  {t("inviter.field.preview_has_media")}
                </Badge>
              )}
            </span>
          </div>
          {props.forwardPreview.text ? (
            <>
              <p className="text-xs text-fg-muted">
                {t("inviter.field.preview_select_hint")}
              </p>
              <div
                onMouseUp={props.onPreviewSelect}
                className="max-h-48 cursor-text select-text overflow-y-auto whitespace-pre-wrap rounded-md border border-border/60 bg-bg-secondary/40 p-2 text-sm"
              >
                {props.forwardPreview.text}
              </div>
            </>
          ) : (
            <p className="text-xs italic text-fg-muted">
              {t("inviter.field.preview_empty_text")}
            </p>
          )}
        </Card>
      )}

      <div className="space-y-2">
        <Label htmlFor="c_fwd_followup">
          {props.forwardQuoteEnabled
            ? t("inviter.field.forward_quote_body")
            : t("inviter.field.forward_followup")}
        </Label>
        <textarea
          id="c_fwd_followup"
          value={props.forwardFollowup}
          onChange={(e) => props.setForwardFollowup(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder={
            props.forwardQuoteEnabled ? "Это про нас 👀" : "👑 Лучший!  👑"
          }
          className={TEXTAREA_CLASS}
        />
        <p className="text-xs text-fg-muted">
          {props.forwardQuoteEnabled
            ? t("inviter.field.forward_quote_body_hint")
            : t("inviter.field.forward_followup_hint")}
        </p>
      </div>

      <Switch
        checked={props.forwardQuoteEnabled}
        onCheckedChange={(v) => props.setForwardQuoteEnabled(!!v)}
        label={t("inviter.field.forward_quote_enable")}
        description={t("inviter.field.forward_quote_enable_hint")}
      />

      {props.forwardQuoteEnabled && (
        <div className="space-y-2 border-l-2 border-accent/40 pl-4">
          <Label htmlFor="c_fwd_quote">{t("inviter.field.forward_quote_text")}</Label>
          <textarea
            id="c_fwd_quote"
            value={props.forwardQuoteText}
            onChange={(e) => props.setForwardQuoteText(e.target.value)}
            rows={3}
            maxLength={1024}
            placeholder={t("inviter.field.forward_quote_text_placeholder") ?? ""}
            className={TEXTAREA_CLASS}
          />
          <p className="text-xs text-fg-muted">
            {t("inviter.field.forward_quote_text_hint")}
          </p>
        </div>
      )}
    </div>
  );
}

// ----- Step 3: audience + delays -----

function Step3({
  sourceChannels,
  setSourceChannels,
  sourceImports,
  setSourceImports,
  maxTargets,
  setMaxTargets,
  minDelay,
  setMinDelay,
  maxDelay,
  setMaxDelay,
  hasUsername,
  setHasUsername,
  onlyPremium,
  setOnlyPremium,
  onlineRecently,
  setOnlineRecently,
  excludeContacted,
  setExcludeContacted,
  excludeContactedDays,
  setExcludeContactedDays,
  excludeToxic,
  setExcludeToxic,
}: {
  sourceChannels: string[];
  setSourceChannels: (v: string[]) => void;
  sourceImports: string[];
  setSourceImports: (v: string[]) => void;
  maxTargets: string;
  setMaxTargets: (v: string) => void;
  minDelay: string;
  setMinDelay: (v: string) => void;
  maxDelay: string;
  setMaxDelay: (v: string) => void;
  hasUsername: boolean;
  setHasUsername: (v: boolean) => void;
  onlyPremium: boolean;
  setOnlyPremium: (v: boolean) => void;
  onlineRecently: boolean | undefined;
  setOnlineRecently: (v: boolean | undefined) => void;
  excludeContacted: boolean;
  setExcludeContacted: (v: boolean) => void;
  excludeContactedDays: string;
  setExcludeContactedDays: (v: string) => void;
  excludeToxic: boolean;
  setExcludeToxic: (v: boolean) => void;
}) {
  const { t } = useTranslation();

  // online filter as a tri-state select (false=long-dormant, true=active, ""=any)
  const onlineValue =
    onlineRecently === false ? "dormant" : onlineRecently === true ? "active" : "any";

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-sm font-medium text-fg-primary">
          {t("inviter.source.group")}
        </p>
        <SourceChannelPicker
          channelIds={sourceChannels}
          importSources={sourceImports}
          onChange={(next) => {
            setSourceChannels(next.channelIds);
            setSourceImports(next.importSources);
          }}
        />
        <p className="text-xs text-fg-muted">{t("inviter.source.hint")}</p>
      </div>

      <div className="space-y-3 border-t border-border pt-5">
        <p className="text-sm font-medium text-fg-primary">
          {t("inviter.redesign.delays_group")}
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="c_max">{t("inviter.field.max_targets")}</Label>
            <Input
              id="c_max"
              inputMode="numeric"
              value={maxTargets}
              onChange={(e) => setMaxTargets(e.target.value.replace(/\D/g, ""))}
              className="font-mono"
            />
          </div>
          <SecondsField
            id="c_min"
            label={t("inviter.field.min_delay")}
            value={minDelay}
            onChange={setMinDelay}
          />
          <SecondsField
            id="c_max_delay"
            label={t("inviter.field.max_delay")}
            value={maxDelay}
            onChange={setMaxDelay}
          />
        </div>
      </div>

      <div className="space-y-3 border-t border-border pt-5">
        <p className="text-sm font-medium text-fg-primary">
          {t("inviter.redesign.filters_group")}
        </p>
        <Switch
          checked={hasUsername}
          onCheckedChange={(v) => setHasUsername(!!v)}
          label={t("inviter.filters.has_username")}
        />
        <Switch
          checked={onlyPremium}
          onCheckedChange={(v) => setOnlyPremium(!!v)}
          label={t("inviter.filters.only_premium")}
        />
        <div className="space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Switch
                checked={excludeContacted}
                onCheckedChange={(v) => setExcludeContacted(!!v)}
                label={t("inviter.filters.exclude_contacted")}
              />
              <p className="mt-1 text-xs text-fg-muted">
                {t("inviter.filters.exclude_contacted_hint")}
              </p>
            </div>
            <div className="w-28 shrink-0">
              <Input
                inputMode="numeric"
                value={excludeContactedDays}
                onChange={(e) =>
                  setExcludeContactedDays(e.target.value.replace(/\D/g, ""))
                }
                disabled={!excludeContacted}
                className="font-mono text-right"
                aria-label={t("inviter.filters.exclude_contacted_days_label")}
              />
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <Switch
            checked={excludeToxic}
            onCheckedChange={(v) => setExcludeToxic(!!v)}
            label={t("inviter.filters.exclude_toxic")}
          />
          <p className="mt-1 text-xs text-fg-muted">
            {t("inviter.filters.exclude_toxic_hint")}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="c_online">{t("inviter.filters.last_seen_label")}</Label>
          <SimpleSelect
            value={onlineValue}
            onValueChange={(v) =>
              setOnlineRecently(
                v === "dormant" ? false : v === "active" ? true : undefined,
              )
            }
            options={[
              { value: "dormant", label: t("inviter.filters.long_dormant") },
              { value: "active", label: t("inviter.filters.online_recently") },
              { value: "any", label: t("inviter.filters.any_online") },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

/** Numeric input with a "сек" addon suffix. */
function SecondsField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
          className="pr-12 font-mono"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-fg-muted">
          {t("inviter.redesign.seconds_unit")}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer — stat cards + paginated attempts + status filter
// ---------------------------------------------------------------------------

const ATTEMPT_PAGE = 50;

const ATTEMPT_STATUSES: AttemptStatus[] = [
  "pending",
  "added",
  "privacy_restricted",
  "peer_flood",
  "admin_required",
  "flood_wait",
  "channel_private",
  "already_member",
  "deleted_user",
  "too_many_users",
  "other_failed",
  "skipped",
];

function CampaignDetailDrawer({
  id,
  open,
  onOpenChange,
}: {
  id: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const detail = useCampaign(open ? id : null);
  const [statusFilter, setStatusFilter] = useState<AttemptStatus | "">("");
  const [offset, setOffset] = useState(0);

  const live = detail.data?.status === "queued" || detail.data?.status === "running";
  const attempts = useCampaignAttempts(
    open ? id : null,
    { status: statusFilter || undefined, limit: ATTEMPT_PAGE, offset },
    live,
  );

  const c = detail.data;
  const failed = c
    ? c.failed_privacy + c.failed_peer_flood + c.failed_admin + c.failed_other
    : 0;
  const successRate =
    c && c.attempted > 0 ? Math.round((c.succeeded / c.attempted) * 100) : 0;

  const total = attempts.data?.total ?? 0;
  const items = attempts.data?.items ?? [];

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" size="lg">
        <DrawerHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <DrawerTitle className="truncate">{c?.name ?? "…"}</DrawerTitle>
                {c && (
                  <StatusBadge
                    domain="campaign"
                    value={c.status}
                    label={t(`inviter.status.${c.status}`)}
                    dot
                  />
                )}
              </div>
              {c && (
                <DrawerDescription className="font-mono">
                  {c.target_channel_input}
                </DrawerDescription>
              )}
              {c && (
                <p className="text-xs text-fg-muted">
                  {t("inviter.redesign.detail_meta", {
                    strategy: t(`inviter.redesign.strategy_short.${c.strategy}`),
                    created: formatRelative(c.created_at),
                  })}
                </p>
              )}
            </div>
            <DrawerClose
              className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label={t("inviter.redesign.detail_close")}
            >
              <X size={18} strokeWidth={2} aria-hidden="true" />
            </DrawerClose>
          </div>
        </DrawerHeader>

        <DrawerBody className="space-y-5">
          {c && c.error && <Alert tone="danger">{c.error}</Alert>}

          {c && (
            <>
              {/* Priority stat cards: succeeded / failed accented. */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard
                  label={t("inviter.stat.succeeded")}
                  value={c.succeeded}
                  tone="success"
                />
                <StatCard
                  label={t("inviter.stat.failed")}
                  value={failed}
                  tone="danger"
                />
                <StatCard
                  label={t("inviter.stat.attempted")}
                  value={`${c.attempted} / ${c.total_targets}`}
                />
                <StatCard
                  label={t("inviter.redesign.stat_success_rate")}
                  value={`${successRate}%`}
                />
              </div>

              {/* Failure breakdown. */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard label={t("inviter.stat.privacy")} value={c.failed_privacy} muted />
                <StatCard
                  label={t("inviter.stat.peer_flood")}
                  value={c.failed_peer_flood}
                  muted
                />
                <StatCard label={t("inviter.stat.admin")} value={c.failed_admin} muted />
                <StatCard label={t("inviter.stat.other")} value={c.failed_other} muted />
              </div>
            </>
          )}

          {/* Attempts log */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-[15px] font-semibold text-fg-primary">
                {t("inviter.redesign.attempts_title")}
              </h3>
              <div className="w-48">
                <SimpleSelect
                  value={statusFilter || "__all__"}
                  onValueChange={(v) => {
                    setStatusFilter(v === "__all__" ? "" : (v as AttemptStatus));
                    setOffset(0);
                  }}
                  options={[
                    { value: "__all__", label: t("inviter.redesign.attempts_filter_all") },
                    ...ATTEMPT_STATUSES.map((s) => ({
                      value: s,
                      label: t(`inviter.attempt_status.${s}`),
                    })),
                  ]}
                />
              </div>
            </div>

            {attempts.isLoading && (
              <p className="text-sm text-fg-muted">
                {t("inviter.redesign.attempts_loading")}
              </p>
            )}

            {attempts.data && items.length === 0 && (
              <EmptyState
                title={t("inviter.redesign.attempts_empty")}
                className="py-8"
              />
            )}

            {items.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-bg-secondary text-left text-fg-muted">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t("inviter.col.target")}</th>
                      <th className="px-3 py-2 font-medium">
                        {t("inviter.col.inviter_account")}
                      </th>
                      <th className="px-3 py-2 font-medium">{t("inviter.col.status")}</th>
                      <th className="px-3 py-2 font-medium">{t("inviter.col.when")}</th>
                      <th className="px-3 py-2 font-medium">{t("inviter.col.error")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((a) => (
                      <tr key={a.id} className="border-t border-border">
                        <td className="px-3 py-2">
                          {a.tg_username ? (
                            <span>@{a.tg_username}</span>
                          ) : (
                            <span className="text-fg-muted">
                              {a.target_first_name ?? "—"}
                            </span>
                          )}{" "}
                          <span className="font-mono text-fg-muted">
                            ({a.tg_user_id})
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {a.account_label ?? <span className="text-fg-muted">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          <StatusBadge
                            domain="campaign"
                            value={attemptTone(a.status)}
                            label={t(`inviter.attempt_status.${a.status}`)}
                          />
                        </td>
                        <td className="px-3 py-2 text-fg-muted">
                          {a.attempted_at
                            ? formatDateTime(a.attempted_at)
                            : "—"}
                        </td>
                        <td className="max-w-[16rem] px-3 py-2 text-fg-muted">
                          {a.error ? (
                            <Tooltip content={a.error}>
                              <span className="block truncate">{a.error}</span>
                            </Tooltip>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {total > ATTEMPT_PAGE && (
              <div className="flex items-center justify-between gap-3 text-sm text-fg-muted">
                <span>{t("inviter.redesign.attempts_total", { count: total })}</span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - ATTEMPT_PAGE))}
                  >
                    {t("inviter.redesign.back")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={offset + ATTEMPT_PAGE >= total}
                    onClick={() => setOffset(offset + ATTEMPT_PAGE)}
                  >
                    {t("inviter.redesign.next")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Map an attempt status to a campaign-domain value so StatusBadge picks a
 * sensible tone. "added" → done (green); known failures → failed (red);
 * pending/skipped → neutral via the campaign map's fallback.
 */
function attemptTone(status: AttemptStatus): string {
  if (status === "added") return "done";
  if (status === "pending" || status === "already_member") return "queued";
  if (status === "skipped" || status === "deleted_user") return "draft";
  return "failed";
}

function StatCard({
  label,
  value,
  tone,
  muted = false,
}: {
  label: string;
  value: number | string;
  tone?: "success" | "danger";
  muted?: boolean;
}) {
  const valueClass =
    tone === "success"
      ? "text-success"
      : tone === "danger"
        ? "text-danger"
        : "text-fg-primary";
  return (
    <Card className={muted ? "p-3" : "p-4"}>
      <p className="truncate text-xs font-medium text-fg-muted">{label}</p>
      <p
        className={
          "mt-1 font-mono font-semibold tabular-nums " +
          (muted ? "text-lg " : "text-2xl ") +
          valueClass
        }
      >
        {value}
      </p>
    </Card>
  );
}
